import React, { useCallback, useEffect, useRef, useState } from 'react'
import './NavigationWithSideBar.scss'
import { NavigationSidebar } from './NavigationSidebar'
import classNames from 'classnames'

export interface ScrollNavigationSection {
  id: string
  label: string
  title?: string
  content: React.ReactNode
}

interface ScrollNavigationProps {
  sections: ScrollNavigationSection[]
  defaultActiveSectionId: string
  scrollToSectionId?: string
  sectionClassName?: string
  showNavigation?: boolean
  contentFooter?: React.ReactNode
}

interface ContentAreaProps {
  sections: ScrollNavigationSection[]
  onSectionRegister: (id: string, element: HTMLElement | null) => void
  sectionClassName?: string
  contentFooter?: React.ReactNode
}

const ContentArea = React.memo(({ sections, onSectionRegister, sectionClassName, contentFooter }: ContentAreaProps) => {
  return (
    <div className='scroll-navigation__sections'>
      {sections.map((section) => (
        <section
          key={section.id}
          id={section.id}
          ref={(element) => onSectionRegister(section.id, element)}
          className={classNames('zocks-mb-4 scroll-navigation__section', { [`${sectionClassName}`]: sectionClassName })}
          data-section-id={section.id}
        >
          <h2 className='h4 mb-3 zocks-text-gray-900'>{section.title || section.label}</h2>
          {section.content}
        </section>
      ))}
      {contentFooter ? <div className='scroll-navigation__content-footer'>{contentFooter}</div> : null}
      <div className='scroll-navigation__spacer'></div>
    </div>
  )
})

export const ScrollNavigation: React.FC<ScrollNavigationProps> = ({
  sections = [],
  defaultActiveSectionId,
  scrollToSectionId,
  sectionClassName,
  showNavigation = true,
  contentFooter,
}) => {
  const [activeSection, setActiveSection] = useState<string>(defaultActiveSectionId)

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  const scrollToSectionIdRef = useRef(scrollToSectionId)

  const handleSectionRegister = useCallback((id: string, element: HTMLElement | null) => {
    sectionRefs.current[id] = element
  }, [])

  useEffect(() => {
    if (!scrollToSectionIdRef.current) {
      return
    }
    setActiveSection(scrollToSectionIdRef.current)
    const element = sectionRefs.current[scrollToSectionIdRef.current]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId)

    const element = sectionRefs.current[sectionId]

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100

      const currentSection = sections
        .slice()
        .reverse()
        .find((section) => {
          const element = sectionRefs.current[section.id]
          return element && scrollPosition >= element.offsetTop
        })

      if (currentSection && activeSection !== currentSection.id) {
        setActiveSection(currentSection.id)
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [sections, activeSection])

  return (
    <div className='scroll-navigation__container'>
      {showNavigation && (
        <NavigationSidebar sections={sections.map(({ id, label }) => ({ id, label }))} activeSectionId={activeSection} onSectionClick={scrollToSection} />
      )}
      <ContentArea sections={sections} onSectionRegister={handleSectionRegister} sectionClassName={sectionClassName} contentFooter={contentFooter} />
    </div>
  )
}
